/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";

import { FOLDERS } from "../doseeData/utils/Utils";
import { useAdminContext } from "../../context/admin/AdminContext";
import { getOneCompany } from "../../lib/sysNavigation/SysNavigation";
import { ImageKitUpload } from "../../ui/ImageKitLibrary";
import { useCountryContext } from "../../context/CountryContext";
import {
  TextArea,
  MyMessage,
  InputField,
  PhoneInput,
  SearchSelectInput,
  PasswordField,
  AddPageShell,
  AddSection,
} from "../components/Component";
import {
  FiBriefcase,
  FiImage,
  FiUser,
  FiAlignLeft,
  FiGlobe,
} from "react-icons/fi";

const Edit = () => {
  const { updateBySuperAdmin } = useAdminContext();
  const { countries, fetchCountries } = useCountryContext();

  const company = getOneCompany();
  const company_id = company?.company_id;

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setErrorMessage] = useState<any>(null);

  const admin = company?.Admins?.length > 0 ? company?.Admins[0] : null;

  const [firstName, setFirstName] = useState(admin?.first_name);
  const [lastName, setLastName] = useState(admin?.last_name);
  const [password, setPassword] = useState(null);
  const [email, setEmail] = useState(admin?.email);
  const [phoneNumber, setPhoneNumber] = useState(admin?.phonenumber);

  const [name, setName] = useState(company?.name);
  const [country, setCountry] = useState<any>(company?.country);
  const [website, setWebsite] = useState(company?.website);
  const [thumbnail, setThumbnail] = useState(company?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState(company?.image_cdn_id);
  const [description, setDescription] = useState(company?.description);

  const companyRoutes = "/company";

  const responses = (isError: any, successState: any, msg: any) => {
    setLoading(false);
    setError(isError);
    setSuccess(successState);
    setErrorMessage(msg);
  };

  useEffect(() => {
    if (!countries) fetchCountries();
  }, [countries, country]);

  const isValid = ({ data, value, key }: any) => {
    return data?.find((x: any) => (x?.[key] === value ? true : false));
  };

  const addOneCompanyHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, false, "Image Required");
    } else if (!isValid({ data: countries, value: country, key: "name" })) {
      responses(true, false, "Invalid Country");
    } else await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      admin_id: admin?.admin_id,
      name: name,
      thumbnail: thumbnail,
      image_cdn_id: thumbnailId,
      description: description,
      website: website,
      email: email,
      first_name: firstName,
      last_name: lastName,
      password: password && password !== "" ? password : null,
      phonenumber: phoneNumber,
      country: country,
    };

    const { message, status } = await updateBySuperAdmin(company_id, data);

    if (status === 200 || status === 201) {
      setLoading(false);
      setSuccess(true);
      responses(false, true, message);
    } else {
      responses(true, false, message);
    }
  };

  return (
    <AddPageShell
      backRoute={companyRoutes}
      eyebrow="Tenants • Companies"
      title="Edit Company"
      subtitle="Updated brand assets, company details and the primary admin account."
      icon={<FiBriefcase className="h-5 w-5" />}
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={addOneCompanyHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Brand Assets"
        hint="JPG or PNG. Square logos look best."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={FOLDERS.COMPANY}
        />
      </AddSection>

      <AddSection
        title="Company Details"
        hint="Public-facing identity and home country."
        icon={<FiGlobe className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "company_name",
              title: "Company Name",
              type: "text",
              value: name ? name : "",
              required: true,
              onChange: (e: any) => {
                setName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "website",
              title: "Company Website",
              type: "text",
              required: true,
              value: website ? website : "",
              onChange: (e: any) => {
                setWebsite(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <SearchSelectInput
            title="Country"
            value={country ? country : null}
            required
            options={countries ? countries?.map((x: any) => x?.name) : []}
            onChange={(v: any) => setCountry(v)}
          />
        </div>
      </AddSection>

      <AddSection
        title="Primary Admin"
        hint="The first admin who can sign in and invite teammates."
        icon={<FiUser className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "first_name",
              title: "First Name",
              required: true,
              type: "text",
              value: firstName ? firstName : "",
              onChange: (e: any) => {
                setFirstName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "last_name",
              title: "Last Name",
              type: "text",
              value: lastName ? lastName : "",
              required: true,
              onChange: (e: any) => {
                setLastName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "email",
              title: "Email Address",
              type: "text",
              value: email ? email : "",
              required: true,
              onChange: (e: any) => {
                setEmail(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <PhoneInput
            setPhone={setPhoneNumber}
            phone={phoneNumber}
            required={true}
            country={country}
          />
          <PasswordField
            value={password || ""}
            onChange={(e: any) => {
              setPassword(e.target.value);
              setError(null);
              setSuccess(null);
            }}
            disabled={loading}
            required={false}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="A short summary about the company."
        icon={<FiAlignLeft className="h-4 w-4" />}
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            required: true,
            value: description ? description : "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Edit;
